import { CardTemplate } from './templates/types';
import { fieldRegistry } from './edit-modal/fieldRegistry';
import { FieldDefinition } from './edit-modal/types';
import './edit-modal/registerFields';

export type CardType = 'text' | 'image' | 'imageAudio' | 'imageAudioText';

interface CardData {
  title?: string | null;
  body?: string | null;
  pictures?: Array<{ id: string | number }> | null;
  audios?: Array<{ id: string | number }> | null;
}

interface CardItem {
  [key: string]: any;
  id: string | number;
  __typename?: string;
  position?: number | null;
  title?: string | null;
  body?: string | null;
  pictures?: Array<{ id: string | number }> | null;
  audios?: Array<{ id: string | number }> | null;
}

interface SequenceWithItems {
  id: string | number;
  items?: Array<CardItem> | null;
}

const hasFieldData = (field: FieldDefinition, cardData: any): boolean => {
  const value = fieldRegistry.extractValue(field, cardData);

  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'object' && value && 'id' in value) {
    return !!value.id;
  }

  return true;
};

export const determineCardTypeFromTemplates = (
  cardData: CardData,
  templates: CardTemplate[]
): CardType => {
  if (!templates || templates.length === 0) {
    return 'text';
  }

  if (!cardData) {
    return templates[0].type;
  }

  for (const template of templates) {
    const allFieldsMatch = template.fields.every(field =>
      hasFieldData(field, cardData)
    );

    if (allFieldsMatch) {
      return template.type;
    }
  }

  return templates[0].type;
};

export const findCardInSequence = (
  sequence: SequenceWithItems,
  cardId: string | number
): CardItem | undefined => {
  if (!sequence || !sequence.items) {
    return undefined;
  }

  return sequence.items.find(
    item =>
      (item.__typename === 'Card' || item.typename === 'Card') &&
      String(item.id) === String(cardId)
  );
};

interface PlaceholderAvailabilityState {
  globalPlaceholders: any;
  globalPlaceholdersLoading: boolean;
  globalPlaceholdersError: string | null;
}

export const getAddCardDisabledState = (
  state: PlaceholderAvailabilityState
): { disabled: boolean; reason?: string } => {
  const {
    globalPlaceholders,
    globalPlaceholdersLoading,
    globalPlaceholdersError
  } = state;

  if (globalPlaceholdersLoading) {
    return {
      disabled: true,
      reason: 'Loading placeholder media...'
    };
  }

  if (globalPlaceholdersError) {
    return {
      disabled: true,
      reason:
        'Unable to load placeholder media. Please contact support to resolve this issue.'
    };
  }

  const hasVerticalImage = !!globalPlaceholders?.images?.vertical;
  const hasAudio = !!globalPlaceholders?.audio;
  const requiredPlaceholdersMissing = !hasVerticalImage || !hasAudio;

  if (requiredPlaceholdersMissing) {
    return {
      disabled: true,
      reason: 'Placeholder media not configured. Please contact support.'
    };
  }

  return { disabled: false };
};
